/**
 * Absen Saya — Clock In/Out panel (2026-10-12, user request Tahap 2).
 *
 * - Live camera preview (navigator.mediaDevices.getUserMedia) + capture foto.
 * - Geolokasi (navigator.geolocation) + umur fix.
 * - Peta Leaflet + OpenStreetMap: lingkaran radius lokasi penugasan + marker user.
 * - Validasi server-side; luar radius/akurasi buruk tetap boleh dgn alasan wajib.
 * - Anti double-click: tombol disabled saat submitting.
 * - Semua teks Bahasa Indonesia.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, MapPin, Clock, AlertTriangle, CheckCircle2, Loader2, RefreshCw, LogIn, LogOut, Info } from "lucide-react";
import { MapContainer, TileLayer, Marker, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const officeIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41],
});
const userIcon = new L.DivIcon({
  className: "att-user-marker",
  html: '<div style="width:16px;height:16px;border-radius:50%;background:#FF5A1F;border:3px solid #fff;box-shadow:0 0 0 2px #FF5A1F;"></div>',
  iconSize: [16, 16], iconAnchor: [8, 8],
});

function Recenter({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.setView(center, 17); }, [center, map]);
  return null;
}

function haversine(a, b) {
  if (!a || !b) return null;
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLon = toRad(b[1] - a[1]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch { return iso; }
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("id-ID", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  } catch { return iso; }
}

export default function AbsenSayaPanel() {
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [serverTime, setServerTime] = useState(null);
  const [geo, setGeo] = useState(null);  // {lat, lon, accuracy, timestamp}
  const [geoErr, setGeoErr] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [camActive, setCamActive] = useState(false);
  const [camErr, setCamErr] = useState(null);
  const [photoBlob, setPhotoBlob] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const loadToday = useCallback(async () => {
    try {
      setLoading(true); setErr(null);
      const r = await api.get("/attendance/my-today");
      setToday(r.data);
      setServerTime(r.data.server_time);
    } catch (e) { setErr(formatDetail(e.response?.data?.detail) || "Gagal memuat data absensi."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadToday(); }, [loadToday]);

  // Tick jam server tiap detik berdasarkan selisih client-server (anti drift).
  useEffect(() => {
    if (!serverTime) return;
    const anchor = Date.now();
    const srv0 = new Date(serverTime).getTime();
    const iv = setInterval(() => setServerTime(new Date(srv0 + (Date.now() - anchor)).toISOString()), 1000);
    return () => clearInterval(iv);
  }, [serverTime ? serverTime.slice(0, 19) : null]);  // eslint-disable-line react-hooks/exhaustive-deps

  const requestGeo = useCallback(() => {
    if (!navigator.geolocation) { setGeoErr("Browser tidak mendukung geolokasi."); return; }
    setGeoLoading(true); setGeoErr(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeo({ lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy: pos.coords.accuracy, timestamp: pos.timestamp });
        setGeoLoading(false);
      },
      (e) => { setGeoErr(`Gagal mengambil lokasi: ${e.message}`); setGeoLoading(false); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  }, []);

  const startCamera = useCallback(async () => {
    try {
      setCamErr(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCamActive(true);
    } catch (e) {
      setCamErr(`Gagal akses kamera: ${e.message || e.name}. Pastikan izin kamera diberikan.`);
      setCamActive(false);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCamActive(false);
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);  // cleanup on unmount

  const takePhoto = useCallback(() => {
    const v = videoRef.current, c = canvasRef.current;
    if (!v || !c) return;
    const w = v.videoWidth || 640, h = v.videoHeight || 480;
    c.width = w; c.height = h;
    const ctx = c.getContext("2d");
    ctx.drawImage(v, 0, 0, w, h);
    c.toBlob((blob) => {
      if (!blob) { toast.error("Gagal menyimpan foto."); return; }
      setPhotoBlob(blob);
      if (photoPreview) URL.revokeObjectURL(photoPreview);
      setPhotoPreview(URL.createObjectURL(blob));
    }, "image/jpeg", 0.85);
  }, [photoPreview]);

  const resetPhoto = useCallback(() => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(null); setPhotoBlob(null);
  }, [photoPreview]);

  const loc = today?.location;
  const asg = today?.assignment;
  const mode = today?.can_clock_out ? "out" : "in";
  const openBreak = today?.open_break;
  const PRESENCE_BADGE = {
    in: { label: "Di Lokasi", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    tugas_luar: { label: "Tugas Luar", cls: "text-sky-700 bg-sky-50 border-sky-200" },
    keluar_pribadi: { label: "Keluar Pribadi", cls: "text-amber-700 bg-amber-50 border-amber-200" },
    out: { label: "Sudah Pulang", cls: "text-slate-600 bg-slate-100 border-slate-300" },
    unknown: { label: "Belum Absen", cls: "text-slate-500 bg-slate-50 border-slate-200" },
  };
  const presenceBadge = PRESENCE_BADGE[today?.latest_presence || "unknown"];
  const needPhoto = mode === "in" ? (asg?.require_photo_in ?? true) : (asg?.require_photo_out ?? false);

  // Hitung indikator UI (persis sama dgn backend).
  const distance = useMemo(() => {
    if (!geo || !loc?.latitude) return null;
    return haversine([loc.latitude, loc.longitude], [geo.lat, geo.lon]);
  }, [geo, loc]);
  const inRadius = distance != null && loc?.radius_m != null && distance <= loc.radius_m;
  const accurate = geo && loc?.max_accuracy_m != null && geo.accuracy <= loc.max_accuracy_m;
  const locAgeMs = geo ? Math.max(0, Date.now() - geo.timestamp) : 0;
  const fresh = geo && locAgeMs <= 60_000;
  const anomalies = [];
  if (geo && distance != null && !inRadius) anomalies.push(`Di luar radius (${Math.round(distance)} m, batas ${loc.radius_m} m)`);
  if (geo && !accurate) anomalies.push(`Akurasi GPS rendah (${Math.round(geo.accuracy)} m, batas ${loc.max_accuracy_m} m)`);
  if (geo && !fresh) anomalies.push("Data lokasi tidak fresh (>60 detik)");
  const needsReview = anomalies.length > 0;

  const canSubmit = !submitting && geo && !!today && (today.can_clock_in || today.can_clock_out)
    && (!needPhoto || !!photoBlob)
    && (!needsReview || reason.trim().length >= 5);

  const doSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("kind", mode);
      fd.append("latitude", String(geo.lat));
      fd.append("longitude", String(geo.lon));
      fd.append("accuracy", String(geo.accuracy));
      fd.append("location_age_ms", String(locAgeMs));
      if (reason.trim()) fd.append("reason", reason.trim());
      if (photoBlob) fd.append("photo", photoBlob, `absen_${mode}_${Date.now()}.jpg`);
      await api.post("/attendance/clock", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(mode === "in" ? "Absen masuk tersimpan." : "Absen pulang tersimpan.");
      stopCamera(); resetPhoto(); setReason("");
      await loadToday();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal menyimpan absen."); }
    finally { setSubmitting(false); }
  }, [canSubmit, mode, geo, locAgeMs, reason, photoBlob, stopCamera, resetPhoto, loadToday]);

  if (loading) return <div className="p-6 text-center text-slate-500 text-xs">Memuat data absensi…</div>;
  if (err) return <div className="p-6 text-center text-rose-700 text-xs">{err}</div>;

  // Belum ada penugasan aktif.
  if (!asg) {
    return (
      <div className="border border-amber-200 bg-amber-50 p-4 flex items-start gap-2" data-testid="att-no-assignment">
        <AlertTriangle size={16} className="text-amber-700 mt-0.5" />
        <div className="text-xs text-amber-800">
          <div className="font-semibold uppercase tracking-wider">Belum Ada Penugasan Aktif</div>
          <div className="mt-1">Hubungi HR untuk mendapatkan penugasan lokasi &amp; jadwal kerja sebelum dapat absen.</div>
        </div>
      </div>
    );
  }
  // Konfigurasi lokasi belum lengkap.
  const locMissing = loc && ["latitude", "longitude", "radius_m", "max_accuracy_m"].filter((k) => loc[k] == null);
  if (locMissing && locMissing.length) {
    return (
      <div className="border border-rose-200 bg-rose-50 p-4 flex items-start gap-2" data-testid="att-loc-incomplete">
        <AlertTriangle size={16} className="text-rose-700 mt-0.5" />
        <div className="text-xs text-rose-800">
          <div className="font-semibold uppercase tracking-wider">Konfigurasi Lokasi Belum Lengkap</div>
          <div className="mt-1">Lokasi "{loc.name}" belum memiliki: {locMissing.join(", ")}. Hubungi HR.</div>
        </div>
      </div>
    );
  }

  const event = today.event;
  const alreadyDone = event && event.clock_out_at;
  const center = geo ? [geo.lat, geo.lon] : [loc.latitude, loc.longitude];

  return (
    <div className="space-y-4" data-testid="att-absen-panel">
      {/* Header info */}
      <div className="border border-slate-200 bg-white p-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{fmtDate(today.today)}</div>
            <div className="font-display text-sm text-[#0B1D3A] mt-0.5">{loc.name} <span className="text-[10px] text-slate-400">· {loc.timezone}</span></div>
            {asg.is_temporary && <span className="inline-block mt-1 text-[9px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5">Penugasan Sementara</span>}
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Jam Server</div>
            <div className="font-mono text-lg text-[#0B1D3A] tabular-nums" data-testid="att-server-clock">{fmtTime(serverTime)}</div>
            <span className={`inline-block mt-1 text-[9px] uppercase tracking-wider font-semibold border px-1.5 py-0.5 ${presenceBadge.cls}`} data-testid="att-presence-badge">{presenceBadge.label}</span>
          </div>
        </div>
        {openBreak && (
          <div className="mt-3 border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800 flex items-start gap-2" data-testid="att-open-break-warning">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>Sesi keluar sementara masih aktif ({openBreak.kind_label} · {openBreak.destination}). Absen Pulang terkunci sampai sesi diselesaikan di tab Keluar &amp; Tugas Luar.</span>
          </div>
        )}
        {event && (
          <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 gap-3 text-[10px]">
            <div>
              <div className="uppercase tracking-wider text-slate-500">Absen Masuk</div>
              <div className="font-mono text-sm tabular-nums" data-testid="att-event-in">{fmtTime(event.clock_in_at)}</div>
              <div className="text-slate-500">Jarak {Math.round(event.clock_in_distance_m)} m · akurasi {Math.round(event.clock_in_accuracy_m)} m</div>
            </div>
            <div>
              <div className="uppercase tracking-wider text-slate-500">Absen Pulang</div>
              <div className="font-mono text-sm tabular-nums" data-testid="att-event-out">{event.clock_out_at ? fmtTime(event.clock_out_at) : <span className="text-slate-400 italic">Belum</span>}</div>
              {event.clock_out_at && <div className="text-slate-500">Jarak {Math.round(event.clock_out_distance_m)} m · akurasi {Math.round(event.clock_out_accuracy_m)} m</div>}
            </div>
          </div>
        )}
      </div>

      {alreadyDone ? (
        <div className="border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-2" data-testid="att-done">
          <CheckCircle2 size={16} className="text-emerald-700 mt-0.5" />
          <div className="text-xs text-emerald-800">
            <div className="font-semibold uppercase tracking-wider">Absensi Hari Ini Selesai</div>
            <div className="mt-1">Masuk {fmtTime(event.clock_in_at)} · Pulang {fmtTime(event.clock_out_at)}. Terima kasih.</div>
          </div>
        </div>
      ) : (
        <>
          {/* Peta + status geo */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border border-slate-200">
              <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold flex items-center gap-1"><MapPin size={12} /> Lokasi Anda</div>
                <Button size="sm" variant="outline" onClick={requestGeo} disabled={geoLoading} className="rounded-none h-7 text-[10px]" data-testid="att-geo-refresh">
                  {geoLoading ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />} Ambil Lokasi
                </Button>
              </div>
              <div style={{ height: 240 }} data-testid="att-map">
                <MapContainer center={center} zoom={16} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
                  <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <Marker position={[loc.latitude, loc.longitude]} icon={officeIcon} />
                  <Circle center={[loc.latitude, loc.longitude]} radius={loc.radius_m} pathOptions={{ color: "#0B1D3A", fillColor: "#0B1D3A", fillOpacity: 0.08 }} />
                  {geo && <Marker position={[geo.lat, geo.lon]} icon={userIcon} />}
                  {geo && <Recenter center={[geo.lat, geo.lon]} />}
                </MapContainer>
              </div>
              <div className="px-3 py-2 text-[10px] space-y-0.5">
                {geoErr && <div className="text-rose-700" data-testid="att-geo-err">{geoErr}</div>}
                {geo ? (
                  <>
                    <div>Koordinat: <span className="font-mono">{geo.lat.toFixed(6)}, {geo.lon.toFixed(6)}</span></div>
                    <div>Jarak ke lokasi: <span className={`font-mono ${inRadius ? "text-emerald-700" : "text-rose-700"}`} data-testid="att-geo-distance">{Math.round(distance)} m</span> (batas {loc.radius_m} m)</div>
                    <div>Akurasi GPS: <span className={`font-mono ${accurate ? "text-emerald-700" : "text-amber-700"}`} data-testid="att-geo-accuracy">{Math.round(geo.accuracy)} m</span> (batas {loc.max_accuracy_m} m)</div>
                    <div>Umur data: <span className={`font-mono ${fresh ? "text-emerald-700" : "text-amber-700"}`}>{Math.round(locAgeMs / 1000)} dtk</span></div>
                  </>
                ) : <div className="text-slate-500 italic">Klik "Ambil Lokasi" untuk membaca GPS browser Anda.</div>}
              </div>
            </div>

            {/* Kamera + foto */}
            <div className="border border-slate-200">
              <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold flex items-center gap-1"><Camera size={12} /> Foto Bukti {needPhoto ? <span className="text-rose-600">*</span> : <span className="text-slate-400">(opsional)</span>}</div>
                {!camActive && !photoBlob && (
                  <Button size="sm" variant="outline" onClick={startCamera} className="rounded-none h-7 text-[10px]" data-testid="att-cam-start">Aktifkan Kamera</Button>
                )}
                {camActive && !photoBlob && (
                  <Button size="sm" onClick={takePhoto} className="rounded-none h-7 text-[10px] bg-[#FF5A1F] hover:bg-[#e04a13]" data-testid="att-cam-capture">Ambil Foto</Button>
                )}
                {photoBlob && (
                  <Button size="sm" variant="outline" onClick={() => { resetPhoto(); if (!camActive) startCamera(); }} className="rounded-none h-7 text-[10px]" data-testid="att-cam-retake">Ulangi</Button>
                )}
              </div>
              <div className="bg-slate-900 relative" style={{ height: 240 }}>
                {!camActive && !photoBlob && (
                  <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-[11px] p-4 text-center">
                    {camErr || "Klik \"Aktifkan Kamera\" untuk memulai preview."}
                  </div>
                )}
                {camActive && !photoBlob && (
                  <video ref={videoRef} className="w-full h-full object-cover" playsInline muted data-testid="att-cam-video" />
                )}
                {photoBlob && photoPreview && (
                  <img src={photoPreview} alt="Foto bukti absen" className="w-full h-full object-contain bg-slate-900" data-testid="att-cam-preview" />
                )}
                <canvas ref={canvasRef} className="hidden" />
              </div>
              <div className="px-3 py-2 text-[10px] text-slate-500">
                {photoBlob ? <span className="text-emerald-700">Foto siap ({Math.round(photoBlob.size / 1024)} KB).</span>
                  : needPhoto ? <span className="text-rose-600">Foto wajib diambil sebelum submit.</span>
                    : "Foto opsional sesuai konfigurasi penugasan Anda."}
              </div>
            </div>
          </div>

          {/* Alasan jika anomali */}
          {needsReview && (
            <div className="border border-amber-200 bg-amber-50 p-3" data-testid="att-reason-block">
              <div className="flex items-start gap-2 mb-2">
                <AlertTriangle size={14} className="text-amber-700 mt-0.5" />
                <div className="text-[11px] text-amber-800">
                  <div className="font-semibold uppercase tracking-wider">Perlu Verifikasi</div>
                  <ul className="list-disc list-inside mt-0.5">{anomalies.map((a, i) => <li key={i}>{a}</li>)}</ul>
                  <div className="mt-1 italic">Anda tetap dapat absen, namun wajib menuliskan alasan (minimal 5 karakter) — approver akan meninjau.</div>
                </div>
              </div>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Tuliskan alasan kondisi di atas (mis. 'Tugas luar ke klien X', 'GPS lemah di dalam gedung')…"
                rows={3} className="rounded-none text-xs" data-testid="att-reason-input" />
            </div>
          )}

          {/* Tombol submit */}
          <div className="flex items-center justify-between gap-3 border-t border-slate-100 pt-3">
            <div className="text-[10px] text-slate-500 flex items-center gap-1">
              <Info size={10} /> {mode === "in" ? "Absen Masuk" : "Absen Pulang"}
              {anomalies.length === 0 && geo && <span className="ml-2 text-emerald-700"><CheckCircle2 size={10} className="inline" /> Semua indikator valid</span>}
            </div>
            <Button
              size="lg"
              onClick={doSubmit}
              disabled={!canSubmit}
              className={`rounded-none ${mode === "in" ? "bg-[#0B1D3A] hover:bg-[#0b1d3ad9]" : "bg-[#FF5A1F] hover:bg-[#e04a13]"}`}
              data-testid={`att-submit-${mode}`}
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : (mode === "in" ? <LogIn size={16} /> : <LogOut size={16} />)}
              {mode === "in" ? "Absen Masuk" : "Absen Pulang"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
